import React, { useRef, useEffect } from 'react';

interface MagnifierCanvasProps {
  wheelBitmap: HTMLCanvasElement | null;
  pointerX: number;
  pointerY: number;
  magnification?: number;
  lensRadius?: number;
  visible: boolean;
}

const MagnifierCanvas: React.FC<MagnifierCanvasProps> = ({
  wheelBitmap,
  pointerX,
  pointerY,
  magnification = 3,
  lensRadius = 55,
  visible,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const diameter = lensRadius * 2;

  useEffect(() => {
    if (!visible || !wheelBitmap) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = diameter * dpr;
    canvas.height = diameter * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Clear
    ctx.clearRect(0, 0, diameter, diameter);

    // Clip to circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(lensRadius, lensRadius, lensRadius - 2, 0, Math.PI * 2);
    ctx.clip();

    // Draw magnified portion of the wheel bitmap
    const srcSize = diameter / magnification;
    const sx = pointerX - srcSize / 2;
    const sy = pointerY - srcSize / 2;

    ctx.drawImage(
      wheelBitmap,
      sx, sy, srcSize, srcSize,
      0, 0, diameter, diameter,
    );

    ctx.restore();

    // Draw crosshair
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 1;
    const crossSize = 8;
    const cx = lensRadius;
    const cy = lensRadius;

    ctx.beginPath();
    ctx.moveTo(cx - crossSize, cy);
    ctx.lineTo(cx - 3, cy);
    ctx.moveTo(cx + 3, cy);
    ctx.lineTo(cx + crossSize, cy);
    ctx.moveTo(cx, cy - crossSize);
    ctx.lineTo(cx, cy - 3);
    ctx.moveTo(cx, cy + 3);
    ctx.lineTo(cx, cy + crossSize);
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fill();
    ctx.restore();

    // Border ring
    ctx.beginPath();
    ctx.arc(lensRadius, lensRadius, lensRadius - 1, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(lensRadius, lensRadius, lensRadius, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [visible, wheelBitmap, pointerX, pointerY, magnification, lensRadius, diameter]);

  if (!visible) return null;

  // Position: above the pointer by default, flip below if near top
  const offsetY = -lensRadius - 25;
  const flipThreshold = lensRadius + 30;
  const shouldFlipDown = pointerY < flipThreshold;
  const top = shouldFlipDown
    ? pointerY + 25
    : pointerY + offsetY;
  const left = pointerX - lensRadius;

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none"
      style={{
        position: 'absolute',
        top,
        left,
        width: diameter,
        height: diameter,
        borderRadius: '50%',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.3)',
        zIndex: 50,
        transition: 'opacity 0.1s ease',
        opacity: visible ? 1 : 0,
      }}
    />
  );
};

export default MagnifierCanvas;
